import React, { useState, useEffect, useRef } from 'react';
import Footer from '../components/Footer';
import Select from '../components/Select';
import { useInvoice } from '../context/InvoiceContext';
import { settingsService, usersService, creditService, employeeDocService } from '../services/api';
import { formatFileSize } from '../utils/formatCurrency';

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace('/api', '');

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Profile = () => {
  const { currentUser, updateProfile, updatePassword, uploadPhoto, removePhoto, showNotification, isSuperAdmin, branding, loadBranding, updateBranding, permissions, loadPermissions } = useInvoice();
  const photoInputRef = useRef(null);
  const docAadharRef = useRef(null);
  const docOfferRef = useRef(null);
  const docCertRef = useRef(null);
  const [myDocs, setMyDocs] = useState([]);
  const [myDocsLoading, setMyDocsLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    loginId: currentUser?.loginId || '',
    officePhone: currentUser?.officePhone || '',
    personalPhone: currentUser?.personalPhone || '',
    officeEmail: currentUser?.officeEmail || '',
    personalEmail: currentUser?.personalEmail || '',
    address: currentUser?.address || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState('');
  const [defaultPasswordLoaded, setDefaultPasswordLoaded] = useState(false);
  const [isSavingDefaultPassword, setIsSavingDefaultPassword] = useState(false);
  const [savingDefaultPassword, setSavingDefaultPassword] = useState(false);
  const [showDefaultPassword, setShowDefaultPassword] = useState(false);
  const [dbStats, setDbStats] = useState(null);
  const [dbStatsLoading, setDbStatsLoading] = useState(false);
  const [uploadsStats, setUploadsStats] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreFiles, setRestoreFiles] = useState(false);
  const [restoreConfirmText, setRestoreConfirmText] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [brandColors, setBrandColors] = useState({ primaryColor: '', secondaryColor: '' });
  const [logoUploading, setLogoUploading] = useState(false);
  const [colorsSaving, setColorsSaving] = useState(false);
  const [localPerms, setLocalPerms] = useState(null);
  const [userPermUsers, setUserPermUsers] = useState([]);
  const [selectedUserForPerm, setSelectedUserForPerm] = useState('');
  const [userOverridesLoading, setUserOverridesLoading] = useState(false);
  const [creditTxns, setCreditTxns] = useState([]);
  const [creditTxnsLoading, setCreditTxnsLoading] = useState(false);
  const [creditBalanceFetched, setCreditBalanceFetched] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [creditAdding, setCreditAdding] = useState(false);

  useEffect(() => {
    setBrandColors({
      primaryColor: branding?.primaryColor || '',
      secondaryColor: branding?.secondaryColor || ''
    });
  }, [branding]);

  useEffect(() => {
    if (permissions) setLocalPerms(JSON.parse(JSON.stringify(permissions)));
  }, [permissions]);

  useEffect(() => {
    if (isSuperAdmin()) {
      usersService.getUsers()
        .then(data => setUserPermUsers(data.users.filter(u => u.id !== currentUser?.id)))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [userOverrides, setUserOverrides] = useState({});
  useEffect(() => {
    if (!selectedUserForPerm) { setUserOverrides({}); return; }
    setUserOverridesLoading(true);
    settingsService.getUserPermissions(selectedUserForPerm)
      .then(setUserOverrides)
      .catch(() => setUserOverrides({}))
      .finally(() => setUserOverridesLoading(false));
  }, [selectedUserForPerm]);

  useEffect(() => {
    if (currentUser?.id) {
      setCreditTxnsLoading(true);
      creditService.getTransactions(currentUser.id)
        .then(data => {
          setCreditTxns(data.transactions || []);
          if (data.balance !== undefined) setCreditBalanceFetched(parseFloat(data.balance));
        })
        .catch(() => {})
        .finally(() => setCreditTxnsLoading(false));
    }
  }, [currentUser?.id]);

  const handleAddCredit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(creditAmount);
    if (!amt || amt <= 0) return;
    setCreditAdding(true);
    try {
      const result = await creditService.addCredit(currentUser.id, { amount: amt, note: creditNote.trim() || undefined });
      setCreditBalanceFetched(parseFloat(result.newBalance));
      setCreditTxns(prev => [result.transaction, ...prev]);
      setCreditAmount('');
      setCreditNote('');
      showNotification(`₹${amt.toFixed(2)} credit added`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to add credit', 'error');
    } finally {
      setCreditAdding(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') {
      settingsService.get('default_employee_password')
        .then(val => { setDefaultPassword(val || ''); setDefaultPasswordLoaded(true); })
        .catch(() => setDefaultPasswordLoaded(true));

      setDbStatsLoading(true);
      Promise.all([
        settingsService.getDbStats(),
        settingsService.getUploadsStats()
      ])
        .then(([db, uploads]) => { setDbStats(db); setUploadsStats(uploads); })
        .catch(() => {})
        .finally(() => setDbStatsLoading(false));
    }
  }, [currentUser]);

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      await settingsService.downloadBackup();
      showNotification('Backup downloaded successfully', 'success');
    } catch {
      showNotification('Backup failed', 'error');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile || restoreConfirmText !== 'RESTORE') return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const res = await settingsService.restoreBackup(restoreFile, { restoreFiles });
      setRestoreResult({ ok: true, message: res.message, preRestoreBackup: res.preRestoreBackup });
      showNotification('Restore complete — reload to see the restored data', 'success');
      setRestoreFile(null);
      setRestoreConfirmText('');
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    } catch (err) {
      setRestoreResult({ ok: false, message: err.message || 'Restore failed' });
      showNotification(err.message || 'Restore failed', 'error');
    } finally {
      setRestoring(false);
    }
  };

  const handleSaveDefaultPassword = async (e) => {
    e.preventDefault();
    if (defaultPassword.length < 6) {
      showNotification('Default password must be at least 6 characters', 'error');
      return;
    }
    setSavingDefaultPassword(true);
    try {
      await settingsService.set('default_employee_password', defaultPassword);
      setIsSavingDefaultPassword(false);
      showNotification('Default employee password saved', 'success');
    } catch (err) {
      showNotification('Failed to save default password', 'error');
    } finally {
      setSavingDefaultPassword(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;
    setMyDocsLoading(true);
    employeeDocService.getDocuments(currentUser.id)
      .then(data => setMyDocs(data.documents || []))
      .catch(() => {})
      .finally(() => setMyDocsLoading(false));
  }, [currentUser?.id]);

  const handleMyDocUpload = async (type, files) => {
    if (!files || files.length === 0) return;
    try {
      const data = await employeeDocService.uploadDocument(currentUser.id, type, Array.from(files));
      setMyDocs(prev =>
        type === 'certificate'
          ? [...prev, ...data.documents]
          : [...prev.filter(d => d.type !== type), ...data.documents]
      );
      showNotification('Document uploaded', 'success');
    } catch (error) {
      showNotification(error.message || 'Upload failed', 'error');
    }
  };

  const handleMyDocDelete = async (docId) => {
    try {
      await employeeDocService.deleteDocument(currentUser.id, docId);
      setMyDocs(prev => prev.filter(d => d.id !== docId));
      showNotification('Document deleted', 'success');
    } catch (error) {
      showNotification(error.message || 'Delete failed', 'error');
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const success = await updateProfile(currentUser.id, profileData);
    if (success) {
      setIsEditingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showNotification('Passwords do not match', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showNotification('Password must be at least 6 characters', 'error');
      return;
    }

    const success = await updatePassword(currentUser.id, {
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword
    });

    if (success) {
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsChangingPassword(false);
    }
  };

  const getPhotoUrl = (photo) => `${API_URL.replace('/api', '')}/${photo}`;

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      showNotification('Please upload a PNG or JPG image', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification('Image must be less than 5MB', 'error');
      return;
    }
    await uploadPhoto(currentUser.id, file);
    e.target.value = '';
  };

  const handlePhotoRemove = async () => {
    await removePhoto(currentUser.id);
  };

  const handleCancelProfile = () => {
    setProfileData({
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      loginId: currentUser?.loginId || '',
      officePhone: currentUser?.officePhone || '',
      personalPhone: currentUser?.personalPhone || '',
      officeEmail: currentUser?.officeEmail || '',
      personalEmail: currentUser?.personalEmail || '',
      address: currentUser?.address || '',
    });
    setIsEditingProfile(false);
  };

  const handleCancelPassword = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      showNotification('Please upload a PNG or JPG image', 'error');
      return;
    }
    setLogoUploading(true);
    try {
      await settingsService.uploadLogo(file);
      await loadBranding();
      showNotification('Logo uploaded successfully', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to upload logo', 'error');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleResetLogo = async () => {
    setLogoUploading(true);
    try {
      await settingsService.resetLogo();
      await loadBranding();
      showNotification('Logo reset to default', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to reset logo', 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveColors = async (e) => {
    e.preventDefault();
    setColorsSaving(true);
    try {
      await updateBranding({
        primaryColor: brandColors.primaryColor || null,
        secondaryColor: brandColors.secondaryColor || null
      });
    } finally {
      setColorsSaving(false);
    }
  };

  const handlePermToggle = async (role, key) => {
    const newPerms = {
      ...localPerms,
      [role]: { ...localPerms[role], [key]: !localPerms[role][key] }
    };
    setLocalPerms(newPerms);
    try {
      await settingsService.setPermissions(newPerms);
      await loadPermissions();
    } catch (err) {
      setLocalPerms(localPerms);
      showNotification(err.message || 'Failed to update permission', 'error');
    }
  };

  const handleUserPermToggle = async (key) => {
    const selectedUser = userPermUsers.find(u => String(u.id) === String(selectedUserForPerm));
    if (!selectedUser) return;
    const roleDefault = localPerms?.[selectedUser.role]?.[key] ?? false;
    const currentEffective = key in userOverrides ? userOverrides[key] : roleDefault;
    const newValue = !currentEffective;
    const newOverrides = { ...userOverrides };
    if (newValue === roleDefault) {
      delete newOverrides[key];
    } else {
      newOverrides[key] = newValue;
    }
    const prev = userOverrides;
    setUserOverrides(newOverrides);
    try {
      await settingsService.setUserPermissions(selectedUserForPerm, newOverrides);
      showNotification('Permission updated', 'success');
    } catch (err) {
      setUserOverrides(prev);
      showNotification(err.message || 'Failed to update permission', 'error');
    }
  };

  const handleUserRoleChange = async (newRole) => {
    if (!selectedUserForPerm) return;
    try {
      await usersService.updateUserRole(selectedUserForPerm, newRole);
      setUserPermUsers(prev => prev.map(u =>
        String(u.id) === String(selectedUserForPerm) ? { ...u, role: newRole } : u
      ));
      showNotification('Role updated', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to update role', 'error');
    }
  };

  const handleResetUserPerms = async () => {
    const prev = userOverrides;
    setUserOverrides({});
    try {
      await settingsService.setUserPermissions(selectedUserForPerm, {});
      showNotification('User permissions reset to role defaults', 'success');
    } catch {
      setUserOverrides(prev);
      showNotification('Failed to reset permissions', 'error');
    }
  };

  const handleResetColors = async () => {
    setColorsSaving(true);
    try {
      await updateBranding({ primaryColor: null, secondaryColor: null });
      setBrandColors({ primaryColor: '', secondaryColor: '' });
    } finally {
      setColorsSaving(false);
    }
  };

  return (
    <>
    <div className="main-content">
        <div className="container">
          <h1 className="mb-lg">Profile Settings</h1>

          <div className="grid grid-cols-1" style={{ gap: 'var(--spacing-lg)', maxWidth: '800px' }}>
            {/* Profile Photo */}
            <div className="card">
              <div className="card-header">Profile Photo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-lg)' }}>
                <div
                  onClick={() => photoInputRef.current?.click()}
                  style={{ width: '90px', height: '90px', borderRadius: '2px', overflow: 'hidden', backgroundColor: 'var(--color-gray-100)', border: '2px solid var(--color-gray-200)', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Click to change photo"
                >
                  {currentUser?.photo ? (
                    <img src={getPhotoUrl(currentUser.photo)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: '32px', color: 'var(--color-gray-400)' }}>👤</span>
                  )}
                </div>
                <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/jpg" style={{ display: 'none' }} onChange={handlePhotoChange} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  <button onClick={() => photoInputRef.current?.click()} className="btn btn-primary btn-sm">
                    {currentUser?.photo ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {currentUser?.photo && (
                    <button onClick={handlePhotoRemove} className="btn btn-secondary btn-sm">
                      Remove Photo
                    </button>
                  )}
                  <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>PNG or JPG, max 5MB</span>
                </div>
              </div>
            </div>

            {/* Profile Information */}
            <div className="card">
              <div className="card-header">Profile Information</div>

              {!isEditingProfile ? (
                <div>
                  {(() => {
                    const labelStyle = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600', marginBottom: '2px' };
                    const Row = ({ label, value }) => value ? (
                      <div className="mb-md">
                        <div style={labelStyle}>{label}</div>
                        <div style={{ fontSize: 'var(--font-size-base)', whiteSpace: 'pre-wrap' }}>{value}</div>
                      </div>
                    ) : null;
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-md mb-md">
                          <div>
                            <div style={labelStyle}>First Name</div>
                            <div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser?.firstName || '—'}</div>
                          </div>
                          <div>
                            <div style={labelStyle}>Last Name</div>
                            <div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser?.lastName || '—'}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-md mb-md">
                          <div>
                            <div style={labelStyle}>Login ID</div>
                            <div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser?.loginId}</div>
                          </div>
                          <div>
                            <div style={labelStyle}>Role</div>
                            <span className="badge badge-primary">{currentUser?.role}</span>
                          </div>
                        </div>
                        {(currentUser?.employeeNo || currentUser?.position || currentUser?.joinDate) && (
                          <div className="grid grid-cols-2 gap-md mb-md">
                            {currentUser?.employeeNo && <div><div style={labelStyle}>Employee No</div><div style={{ fontSize: 'var(--font-size-base)' }}>#{currentUser.employeeNo}</div></div>}
                            {currentUser?.position && <div><div style={labelStyle}>Position</div><div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser.position}</div></div>}
                            {currentUser?.joinDate && <div><div style={labelStyle}>Join Date</div><div style={{ fontSize: 'var(--font-size-base)' }}>{new Date(currentUser.joinDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div></div>}
                            {currentUser?.employmentType && <div><div style={labelStyle}>Employment</div><div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser.employmentType === 'intern' ? 'Intern' : 'Permanent'}</div></div>}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-md mb-md">
                          {currentUser?.officePhone && <div><div style={labelStyle}>Office Mobile</div><div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser.officePhone}</div></div>}
                          {currentUser?.personalPhone && <div><div style={labelStyle}>Personal Mobile</div><div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser.personalPhone}</div></div>}
                          {currentUser?.officeEmail && <div><div style={labelStyle}>Office Email</div><div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser.officeEmail}</div></div>}
                          {currentUser?.personalEmail && <div><div style={labelStyle}>Personal Email</div><div style={{ fontSize: 'var(--font-size-base)' }}>{currentUser.personalEmail}</div></div>}
                        </div>
                        <Row label="Address" value={currentUser?.address} />
                      </>
                    );
                  })()}
                  <button onClick={() => {
                    setProfileData({
                      firstName: currentUser?.firstName || '',
                      lastName: currentUser?.lastName || '',
                      loginId: currentUser?.loginId || '',
                      officePhone: currentUser?.officePhone || '',
                      personalPhone: currentUser?.personalPhone || '',
                      officeEmail: currentUser?.officeEmail || '',
                      personalEmail: currentUser?.personalEmail || '',
                      address: currentUser?.address || '',
                    });
                    setIsEditingProfile(true);
                  }} className="btn btn-primary">
                    Edit Profile
                  </button>
                </div>
              ) : (
                <form onSubmit={handleProfileSubmit}>
                  <div className="grid grid-cols-2 gap-md">
                    <div className="form-group">
                      <label className="form-label">First Name *</label>
                      <input type="text" name="firstName" className="form-input" value={profileData.firstName} onChange={handleProfileChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input type="text" name="lastName" className="form-input" value={profileData.lastName} onChange={handleProfileChange} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Login ID *</label>
                    <input type="text" name="loginId" className="form-input" value={profileData.loginId} onChange={handleProfileChange} required />
                  </div>

                  <div className="grid grid-cols-2 gap-md">
                    <div className="form-group">
                      <label className="form-label">Office Mobile</label>
                      <input type="tel" name="officePhone" className="form-input" value={profileData.officePhone} onChange={handleProfileChange} placeholder="" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Personal Mobile</label>
                      <input type="tel" name="personalPhone" className="form-input" value={profileData.personalPhone} onChange={handleProfileChange} placeholder="" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-md">
                    <div className="form-group">
                      <label className="form-label">Office Email</label>
                      <input type="email" name="officeEmail" className="form-input" value={profileData.officeEmail} onChange={handleProfileChange} placeholder="" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Personal Email</label>
                      <input type="email" name="personalEmail" className="form-input" value={profileData.personalEmail} onChange={handleProfileChange} placeholder="" />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <textarea name="address" className="form-input" value={profileData.address} onChange={handleProfileChange} rows={3} style={{ resize: 'vertical' }} placeholder="" />
                  </div>

                  <div className="flex gap-md">
                    <button type="submit" className="btn btn-primary">Save Changes</button>
                    <button type="button" onClick={handleCancelProfile} className="btn btn-secondary">Cancel</button>
                  </div>
                </form>
              )}
            </div>

            {/* Documents */}
            <div className="card">
              <div className="card-header">Documents</div>

              {/* Hidden file inputs */}
              <input ref={docAadharRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) handleMyDocUpload('aadhar', [e.target.files[0]]); e.target.value = ''; }} />
              <input ref={docOfferRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) handleMyDocUpload('offer_letter', [e.target.files[0]]); e.target.value = ''; }} />
              <input ref={docCertRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
                onChange={e => { if (e.target.files.length > 0) handleMyDocUpload('certificate', Array.from(e.target.files)); e.target.value = ''; }} />

              {myDocsLoading ? (
                <div className="flex-center gap-sm" style={{ padding: 'var(--spacing-md) 0', color: 'var(--color-text-secondary)' }}>
                  <div className="spinner spinner-sm" /> Loading documents...
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {[
                    { type: 'aadhar', label: 'Aadhaar Card', ref: docAadharRef, multi: false },
                    { type: 'offer_letter', label: 'Offer Letter', ref: docOfferRef, multi: false },
                  ].map(({ type, label, ref }) => {
                    const doc = myDocs.find(d => d.type === type);
                    return (
                      <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-sm) var(--spacing-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-gray-200)' }}>
                        <span style={{ flex: 1, fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>{label}</span>
                        {doc ? (
                          <>
                            <a href={`${API_BASE}/uploads/${doc.filename}`} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={doc.originalName}>{doc.originalName}</a>
                            <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }} onClick={() => ref.current?.click()}>Replace</button>
                            <button type="button" className="btn btn-error btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }} onClick={() => handleMyDocDelete(doc.id)}>Delete</button>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>Not uploaded</span>
                            <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }} onClick={() => ref.current?.click()}>Upload</button>
                          </>
                        )}
                      </div>
                    );
                  })}

                  {/* Educational Certificates */}
                  {(() => {
                    const certs = myDocs.filter(d => d.type === 'certificate');
                    return (
                      <div style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-gray-200)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: certs.length > 0 ? 'var(--spacing-sm)' : 0 }}>
                          <span style={{ flex: 1, fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>
                            Educational Certificates {certs.length > 0 && <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'normal' }}>({certs.length})</span>}
                          </span>
                          <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }} onClick={() => docCertRef.current?.click()}>Add</button>
                        </div>
                        {certs.length === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>No certificates uploaded</span>}
                        {certs.map(cert => (
                          <div key={cert.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-xs)' }}>
                            <a href={`${API_BASE}/uploads/${cert.filename}`} target="_blank" rel="noopener noreferrer"
                              style={{ flex: 1, fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={cert.originalName}>{cert.originalName}</a>
                            <button type="button" className="btn btn-error btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }} onClick={() => handleMyDocDelete(cert.id)}>Delete</button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Change Password */}
            <div className="card">
              <div className="card-header">Change Password</div>

              {!isChangingPassword ? (
                <div>
                  <p className="text-secondary mb-md">
                    Update your password to keep your account secure.
                  </p>
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="btn btn-secondary"
                  >
                    Change Password
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePasswordSubmit}>
                  <div className="form-group">
                    <label className="form-label">Current Password</label>
                    <input
                      type="password"
                      name="currentPassword"
                      className="form-input"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      className="form-input"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength="6"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      className="form-input"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength="6"
                    />
                  </div>

                  <div className="flex gap-md">
                    <button type="submit" className="btn btn-primary">
                      Update Password
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelPassword}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
            {/* Credit Balance */}
            <div className="card">
              <div className="card-header">Credit Balance</div>
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <div className="form-label">Available Balance</div>
                <div style={{ fontSize: '1.75rem', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)' }}>
                  ₹{(creditBalanceFetched !== null ? creditBalanceFetched : parseFloat(currentUser?.creditBalance || 0)).toFixed(2)}
                </div>
              </div>
              <form onSubmit={handleAddCredit} style={{ display: 'flex', gap: '8px', marginBottom: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                <input
                  type="number" min="0.01" step="0.01" placeholder="Amount (₹)"
                  value={creditAmount} onChange={e => setCreditAmount(e.target.value)}
                  className="form-input" style={{ width: '120px', fontSize: '13px', padding: '4px 8px' }}
                />
                <input
                  type="text" placeholder="Note (optional)"
                  value={creditNote} onChange={e => setCreditNote(e.target.value)}
                  className="form-input" style={{ flex: 1, minWidth: '120px', fontSize: '13px', padding: '4px 8px' }}
                />
                <button type="submit" className="btn btn-primary btn-sm" disabled={creditAdding || !creditAmount}>
                  {creditAdding ? 'Adding...' : 'Add Credit'}
                </button>
              </form>
              {creditTxnsLoading ? (
                <div className="flex-center" style={{ padding: 'var(--spacing-md)' }}>
                  <div className="spinner spinner-sm" />
                </div>
              ) : creditTxns.length === 0 ? (
                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', fontStyle: 'italic' }}>No transactions yet.</p>
              ) : (
                <div>
                  <div className="form-label" style={{ marginBottom: 'var(--spacing-sm)' }}>Transaction History</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {creditTxns.map(tx => (
                      <div key={tx.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                        backgroundColor: 'var(--color-gray-50)',
                        border: '1px solid var(--color-gray-200)'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: '2px' }}>
                            <span style={{
                              display: 'inline-block', padding: '1px 8px', borderRadius: '2px',
                              fontSize: '10px', fontWeight: '600', letterSpacing: '0.04em',
                              backgroundColor: tx.type === 'credit' ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
                              color: tx.type === 'credit' ? '#16a34a' : '#DC2626'
                            }}>
                              {tx.type === 'credit' ? 'Credit' : 'Deduction'}
                            </span>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-secondary)' }}>
                              {new Date(tx.createdAt).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>
                            {tx.note || (tx.type === 'credit' ? 'Credit added' : 'Expense deduction')}
                          </div>
                        </div>
                        <div style={{
                          fontWeight: 'var(--font-weight-semibold)',
                          fontSize: 'var(--font-size-md)',
                          color: tx.type === 'credit' ? '#16a34a' : '#DC2626',
                          marginLeft: 'var(--spacing-md)',
                          whiteSpace: 'nowrap'
                        }}>
                          {tx.type === 'credit' ? '+' : '-'}₹{parseFloat(tx.amount).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Database */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
              <div className="card">
                <div className="card-header">Database</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-lg)' }}>
                  <div style={{ display: 'flex', gap: 'var(--spacing-xl)', flexWrap: 'wrap' }}>
                    <div>
                      <div className="form-label">Database</div>
                      {dbStatsLoading ? (
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Loading...</span>
                      ) : (
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
                          {dbStats ? formatFileSize(dbStats.sizeBytes) : '—'}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="form-label">Attachments</div>
                      {dbStatsLoading ? (
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Loading...</span>
                      ) : (
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
                          {uploadsStats ? formatFileSize(uploadsStats.sizeBytes) : '—'}
                          {uploadsStats && (
                            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'normal', marginLeft: '6px' }}>
                              ({uploadsStats.fileCount} file{uploadsStats.fileCount !== 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleBackup}
                    className="btn btn-primary"
                    disabled={backupLoading}
                  >
                    {backupLoading ? (
                      <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Exporting...</span>
                    ) : 'Backup Data'}
                  </button>
                </div>
              </div>
            )}

            {/* Restore from Backup — Super Admin only, destructive */}
            {currentUser?.role === 'super_admin' && (
              <div className="card" style={{ border: '2px solid #FCA5A5' }}>
                <div className="card-header" style={{ color: '#DC2626' }}>Restore from Backup</div>
                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-md)' }}>
                  Upload a ZIP from "Backup Data" above to replace the live database with its contents.
                  A safety snapshot of the current state is saved on the server automatically first, but
                  everyone using the app will see the restored data immediately — this cannot be undone
                  from within the app itself.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)', maxWidth: '480px' }}>
                  <input
                    ref={restoreInputRef}
                    type="file"
                    accept=".zip,application/zip"
                    onChange={e => { setRestoreFile(e.target.files?.[0] || null); setRestoreResult(null); }}
                    disabled={restoring}
                  />
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: 'var(--font-size-sm)' }}>
                    <input
                      type="checkbox"
                      checked={restoreFiles}
                      onChange={e => setRestoreFiles(e.target.checked)}
                      disabled={restoring}
                      style={{ width: 'auto', flexShrink: 0, marginTop: '3px' }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>Also restore uploaded files (overwrites current attachments, documents, photos — otherwise only the database changes)</span>
                  </label>
                  {restoreFile && (
                    <>
                      <label className="form-label" style={{ marginTop: 'var(--spacing-xs)' }}>
                        Type <strong>RESTORE</strong> to confirm
                      </label>
                      <input
                        className="form-input"
                        value={restoreConfirmText}
                        onChange={e => setRestoreConfirmText(e.target.value)}
                        placeholder="RESTORE"
                        disabled={restoring}
                        style={{ maxWidth: '200px' }}
                      />
                    </>
                  )}
                  <button
                    onClick={handleRestore}
                    className="btn"
                    disabled={!restoreFile || restoreConfirmText !== 'RESTORE' || restoring}
                    style={{
                      background: '#DC2626', color: '#fff', alignSelf: 'flex-start',
                      opacity: (!restoreFile || restoreConfirmText !== 'RESTORE') ? 0.5 : 1,
                      cursor: (!restoreFile || restoreConfirmText !== 'RESTORE' || restoring) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {restoring ? (
                      <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Restoring...</span>
                    ) : 'Restore from Backup'}
                  </button>
                  {restoreResult && (
                    <div style={{
                      fontSize: 'var(--font-size-sm)',
                      color: restoreResult.ok ? '#065F46' : '#991B1B',
                      background: restoreResult.ok ? '#D1FAE5' : '#FEE2E2',
                      padding: 'var(--spacing-sm)', borderRadius: 'var(--radius-sm)',
                    }}>
                      {restoreResult.message}
                      {restoreResult.ok && (
                        <>
                          <br />Pre-restore safety snapshot saved on the server as <code>{restoreResult.preRestoreBackup}</code>.
                          <br />
                          <button onClick={() => window.location.reload()} className="btn btn-secondary btn-sm" style={{ marginTop: '6px' }}>
                            Reload Now
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Admin Settings */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && defaultPasswordLoaded && (
              <div className="card">
                <div className="card-header">Admin Settings</div>

                {!isSavingDefaultPassword ? (
                  <div>
                    <div className="mb-md">
                      <div className="form-label">Default New Employee Password</div>
                      <div style={{ fontSize: 'var(--font-size-lg)' }}>
                        {defaultPassword ? '••••••••' : <span className="text-secondary">Not set</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => setIsSavingDefaultPassword(true)}
                      className="btn btn-primary"
                    >
                      {defaultPassword ? 'Change Default Password' : 'Set Default Password'}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSaveDefaultPassword}>
                    <div className="form-group">
                      <label className="form-label">Default New Employee Password</label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <input
                          type={showDefaultPassword ? 'text' : 'password'}
                          className="form-input"
                          value={defaultPassword}
                          onChange={(e) => setDefaultPassword(e.target.value)}
                          placeholder="Min. 6 characters"
                          required
                          minLength={6}
                          autoFocus
                          style={{ paddingRight: '80px' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowDefaultPassword(v => !v)}
                          style={{ position: 'absolute', right: 'var(--spacing-sm)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-secondary)', fontSize: 'var(--font-size-sm)', padding: 0 }}
                        >
                          {showDefaultPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      <small className="text-secondary">
                        This will be pre-filled when you add a new employee. The employee can change it after logging in.
                      </small>
                    </div>
                    <div className="flex gap-md">
                      <button type="submit" className="btn btn-primary" disabled={savingDefaultPassword}>
                        {savingDefaultPassword ? (
                          <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Saving...</span>
                        ) : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsSavingDefaultPassword(false)}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
            {/* Brand Settings — super_admin only */}
            {isSuperAdmin() && (
              <div className="card">
                <div className="card-header">Brand Settings</div>

                {/* Logo */}
                <div className="mb-lg">
                  <div className="form-label" style={{ marginBottom: 'var(--spacing-sm)' }}>App Logo</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
                    <div style={{ width: '100px', height: '50px', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-gray-50)' }}>
                      <img
                        src={branding?.logoUrl ? `${API_BASE}/${branding.logoUrl}` : '/berlinr-logo.png'}
                        alt="Current logo"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        style={{ display: 'none' }}
                        onChange={handleLogoUpload}
                      />
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        className="btn btn-primary btn-sm"
                        disabled={logoUploading}
                      >
                        {logoUploading ? 'Uploading...' : 'Upload New Logo'}
                      </button>
                      {branding?.logoUrl && (
                        <button
                          type="button"
                          onClick={handleResetLogo}
                          className="btn btn-secondary btn-sm"
                          disabled={logoUploading}
                        >
                          Reset to Default
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Colors */}
                <form onSubmit={handleSaveColors}>
                  <div className="form-label" style={{ marginBottom: 'var(--spacing-sm)' }}>Brand Colors</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                      <label className="form-label" style={{ minWidth: '100px', margin: 0 }}>Primary</label>
                      <input
                        type="color"
                        value={/^#[0-9A-Fa-f]{6}$/.test(brandColors.primaryColor) ? brandColors.primaryColor : '#B71C1C'}
                        onChange={(e) => setBrandColors(c => ({ ...c, primaryColor: e.target.value }))}
                        style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={brandColors.primaryColor || ''}
                        onChange={(e) => setBrandColors(c => ({ ...c, primaryColor: e.target.value }))}
                        placeholder="#B71C1C"
                        style={{ maxWidth: '120px', fontFamily: 'monospace' }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                      <label className="form-label" style={{ minWidth: '100px', margin: 0 }}>Secondary</label>
                      <input
                        type="color"
                        value={/^#[0-9A-Fa-f]{6}$/.test(brandColors.secondaryColor) ? brandColors.secondaryColor : '#6B7280'}
                        onChange={(e) => setBrandColors(c => ({ ...c, secondaryColor: e.target.value }))}
                        style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={brandColors.secondaryColor || ''}
                        onChange={(e) => setBrandColors(c => ({ ...c, secondaryColor: e.target.value }))}
                        placeholder="#6B7280"
                        style={{ maxWidth: '120px', fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-md">
                    <button type="submit" className="btn btn-primary" disabled={colorsSaving}>
                      {colorsSaving ? (
                        <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Saving...</span>
                      ) : 'Save Colors'}
                    </button>
                    <button type="button" onClick={handleResetColors} className="btn btn-secondary" disabled={colorsSaving}>
                      Reset to Defaults
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Role Permissions — super_admin only, interactive */}
            {isSuperAdmin() && localPerms && (() => {
              const TOGGLEABLE = [
                { key: 'create_invoice',           label: 'Create invoices' },
                { key: 'view_all_invoices',        label: 'View all invoices' },
                { key: 'edit_delete_any_invoice',  label: 'Edit / delete any invoice' },
                { key: 'view_analytics',           label: 'View analytics' },
                { key: 'manage_employees',         label: 'Manage employees' },
                { key: 'reset_employee_passwords', label: 'Reset employee passwords' },
                { key: 'change_roles',             label: 'Change roles' },
                { key: 'set_default_password',     label: 'Set default employee password' },
                { key: 'db_backup',                label: 'DB backup / stats' },
                { key: 'delete_customer',          label: 'Delete customers' },
                { key: 'manage_interviews',        label: 'Manage interviews' },
                { key: 'manage_leads',             label: 'Manage leads' },
              ];
              const th = { padding: '8px 12px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', borderBottom: '2px solid var(--color-gray-200)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap', textAlign: 'center' };
              const td = { padding: '8px 12px', fontSize: 'var(--font-size-sm)', textAlign: 'center' };
              const green = { ...td, color: '#16a34a', fontWeight: 'var(--font-weight-semibold)' };
              const grey  = { ...td, color: 'var(--color-gray-300)' };
              const rowBg = (i) => ({ borderBottom: '1px solid var(--color-gray-200)', backgroundColor: i % 2 !== 0 ? 'var(--color-gray-50)' : 'transparent' });

              // Selected user for per-user overrides
              const selectedUser = userPermUsers.find(u => String(u.id) === String(selectedUserForPerm));
              const roleDefaults = selectedUser ? (localPerms?.[selectedUser.role] || {}) : {};
              const hasOverrides = Object.keys(userOverrides).length > 0;

              return (
                <>
                  <div className="card">
                    <div className="card-header">Role Permissions</div>
                    <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-md)' }}>
                      Toggle capabilities per role. Super Admin always has full access.
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...th, textAlign: 'left' }}>Capability</th>
                            <th style={th}>Employee</th>
                            <th style={th}>Admin</th>
                            <th style={{ ...th, color: 'var(--color-primary)' }}>Super Admin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {['View own invoices', 'Upload invoices', 'Edit / delete own invoices'].map((label, i) => (
                            <tr key={label} style={rowBg(i)}>
                              <td style={{ ...td, textAlign: 'left' }}>{label}</td>
                              <td style={green}>✓</td>
                              <td style={green}>✓</td>
                              <td style={green}>✓</td>
                            </tr>
                          ))}
                          {TOGGLEABLE.map(({ key, label }, i) => (
                            <tr key={key} style={rowBg(i + 3)}>
                              <td style={{ ...td, textAlign: 'left' }}>{label}</td>
                              <td style={td}>
                                <input type="checkbox" checked={!!localPerms.employee[key]} onChange={() => handlePermToggle('employee', key)}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-primary)' }} />
                              </td>
                              <td style={td}>
                                <input type="checkbox" checked={!!localPerms.admin[key]} onChange={() => handlePermToggle('admin', key)}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-primary)' }} />
                              </td>
                              <td style={green}>✓</td>
                            </tr>
                          ))}
                          {['Assign / demote super admin', 'Upload brand logo', 'Change brand colors'].map((label, i) => (
                            <tr key={label} style={rowBg(i + 3 + TOGGLEABLE.length)}>
                              <td style={{ ...td, textAlign: 'left' }}>{label}</td>
                              <td style={grey}>✗</td>
                              <td style={grey}>✗</td>
                              <td style={green}>✓</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Individual User Permissions */}
                  <div className="card">
                    <div className="card-header">Individual User Permissions</div>
                    <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-lg)' }}>
                      Override permissions for a specific user. User overrides take precedence over role defaults.
                    </p>

                    {/* User selector */}
                    <div className="form-group">
                      <label className="form-label">Select User</label>
                      <Select
                        value={selectedUserForPerm}
                        onChange={v => setSelectedUserForPerm(v)}
                        options={[
                          { value: '', label: '— Select a user —' },
                          ...userPermUsers.map(u => ({
                            value: u.id,
                            label: `${[u.firstName, u.lastName].filter(Boolean).join(' ')}${u.employeeNo ? ` (#${u.employeeNo})` : ''} — ${u.role === 'super_admin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'User'}`,
                          })),
                        ]}
                        style={{ maxWidth: '320px' }}
                      />
                    </div>

                    {selectedUserForPerm && (
                      userOverridesLoading ? (
                        <div className="flex-center" style={{ padding: 'var(--spacing-lg)' }}>
                          <div className="spinner spinner-sm" />
                        </div>
                      ) : (
                        <>
                          {/* Role selector */}
                          {(() => {
                            const selectedUser = userPermUsers.find(u => String(u.id) === String(selectedUserForPerm));
                            const ROLE_OPTS = [
                              { value: 'employee', label: 'User',        color: '#64748B', bg: 'rgba(100,116,139,0.1)' },
                              { value: 'admin',    label: 'Admin',       color: '#2563EB', bg: 'rgba(37,99,235,0.1)'   },
                              { value: 'super_admin', label: 'Super Admin', color: '#B91C1C', bg: 'rgba(185,28,28,0.1)' },
                            ];
                            return (
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: 'var(--spacing-md)', flexWrap: 'wrap',
                                padding: '12px 16px', marginBottom: 'var(--spacing-md)',
                                borderRadius: '2px',
                                border: '1px solid var(--color-gray-200)',
                                background: 'var(--color-gray-50)',
                              }}>
                                <div>
                                  <div style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', marginBottom: '2px' }}>
                                    Role
                                  </div>
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                                    Change the role for this user.
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  {ROLE_OPTS.map(opt => {
                                    const active = selectedUser?.role === opt.value;
                                    return (
                                      <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => !active && handleUserRoleChange(opt.value)}
                                        style={{
                                          padding: '4px 14px', borderRadius: '2px', cursor: active ? 'default' : 'pointer',
                                          fontWeight: '600', fontSize: 'var(--font-size-xs)', letterSpacing: '0.04em',
                                          border: `1px solid ${active ? opt.color : opt.color + '55'}`,
                                          background: active ? opt.bg : 'var(--color-white)',
                                          color: active ? opt.color : opt.color + 'aa',
                                          transition: 'all 0.15s',
                                          outline: active ? `2px solid ${opt.color}44` : 'none',
                                          outlineOffset: '1px',
                                        }}
                                      >
                                        {opt.label}
                                        {active && ' ✓'}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Override summary + reset */}
                          {hasOverrides && (
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)',
                              borderRadius: '2px', padding: '8px 12px', marginBottom: 'var(--spacing-md)',
                              fontSize: 'var(--font-size-sm)'
                            }}>
                              <span style={{ color: '#2563EB' }}>
                                {Object.keys(userOverrides).length} custom override{Object.keys(userOverrides).length !== 1 ? 's' : ''} active for this user
                              </span>
                              <button onClick={handleResetUserPerms} className="btn btn-secondary btn-sm">
                                Reset to role defaults
                              </button>
                            </div>
                          )}

                          {/* Per-capability toggles */}
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th style={{ ...th, textAlign: 'left' }}>Capability</th>
                                  <th style={th}>Role default</th>
                                  <th style={th}>This user</th>
                                </tr>
                              </thead>
                              <tbody>
                                {TOGGLEABLE.map(({ key, label }, i) => {
                                  const roleVal = !!roleDefaults[key];
                                  const isOverridden = key in userOverrides;
                                  const effectiveVal = isOverridden ? userOverrides[key] : roleVal;
                                  return (
                                    <tr key={key} style={{
                                      ...rowBg(i),
                                      ...(isOverridden ? { background: 'rgba(37,99,235,0.04)' } : {})
                                    }}>
                                      <td style={{ ...td, textAlign: 'left' }}>
                                        {label}
                                        {isOverridden && (
                                          <span style={{
                                            marginLeft: '8px', fontSize: '10px', fontWeight: '600',
                                            color: '#2563EB', background: 'rgba(37,99,235,0.1)',
                                            padding: '1px 6px', borderRadius: '2px', letterSpacing: '0.04em'
                                          }}>
                                            CUSTOM
                                          </span>
                                        )}
                                      </td>
                                      <td style={roleVal ? green : grey}>{roleVal ? '✓' : '✗'}</td>
                                      <td style={td}>
                                        <input
                                          type="checkbox"
                                          checked={!!effectiveVal}
                                          onChange={() => handleUserPermToggle(key)}
                                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )
                    )}

                    {!selectedUserForPerm && (
                      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', fontStyle: 'italic' }}>
                        Select a user above to view and customize their permissions.
                      </p>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Profile;
